import { useCallback, useEffect, useState } from 'react'
import type { ReactElement } from 'react'

import { useTranslate } from '../lib/i18n-provider'
import { fetchPublication, updateResidence } from '../lib/residence-client'
import type { PublicationState, ResidenceFailure } from '../lib/residence-client'

/**
 * Of deze woning live staat, en zo niet: wat er nog moet gebeuren.
 *
 * Dit ontbrak volledig. De API accepteert een statuswijziging sinds stage 1 en het
 * dashboard bood hem nergens aan, dus een verhuurder kon zijn woning niet online
 * zetten — de kern van wat dit product belooft. De pilotwoning staat gepubliceerd
 * omdat de seed dat zo heeft gezet (§17.37).
 *
 * # De checklist komt van de API
 *
 * De publicatiecontrole in de backend werpt bij de eerste blokkade en zwijgt over de
 * rest. Wie drie dingen mist, hoorde er één, loste die op, en liep tegen de volgende
 * aan. Dit paneel toont ze alle vier tegelijk — uit dezelfde lijst die de controle
 * gebruikt, zodat het scherm niet iets anders kan zeggen dan de server straks doet.
 *
 * # Offline halen mag altijd
 *
 * Publiceren heeft eisen; terugtrekken niet. Een verhuurder die zijn woning van de
 * site wil halen, hoort dat te kunnen zonder eerst een formulier compleet te maken.
 */

export interface PublicationPanelProps {
  readonly residenceId: string
  readonly busy: boolean
  /** Wordt aangeroepen na een geslaagde wijziging, zodat de pagina kan verversen. */
  readonly onChanged: () => void
}

export function PublicationPanel({
  residenceId,
  busy,
  onChanged,
}: PublicationPanelProps): ReactElement {
  const t = useTranslate()

  const [state, setState] = useState<PublicationState | null>(null)
  const [error, setError] = useState<ResidenceFailure | null>(null)
  const [working, setWorking] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    const result = await fetchPublication(residenceId)
    if (result.ok) {
      setState(result.data)
    }
  }, [residenceId])

  useEffect(() => {
    // Zelfde patroon als elders: de toestand wordt pas ná de await gezet, en de
    // lintregel kan dat statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  async function change(status: 'published' | 'draft'): Promise<void> {
    setWorking(true)
    setError(null)
    const result = await updateResidence(residenceId, { status })
    if (!result.ok) {
      // De melding van de server staat erbij: die noemt precies wat er ontbreekt,
      // en dat is bruikbaarder dan een eigen samenvatting.
      setError(result.error)
    }
    await load()
    onChanged()
    setWorking(false)
  }

  if (state === null) {
    return (
      <p className="page__status" role="status">
        {t('common.state.loading')}
      </p>
    )
  }

  const blocking = state.requirements.filter((requirement) => !requirement.met)
  const live = state.status === 'published'
  const disabled = busy || working

  return (
    <section className="publication" data-status={state.status}>
      <h2 className="page__sectionTitle">{t('dashboard.publication.title')}</h2>

      <p className="publication__state">
        <span className="publication__badge" data-live={live ? 'true' : undefined}>
          {t(`dashboard.publication.status.${state.status}`)}
        </span>
        <span className="publication__explain">
          {t(`dashboard.publication.explain.${live ? 'live' : 'offline'}`)}
        </span>
      </p>

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">{t('dashboard.publication.refused')}</p>
          <code className="notice__detail">{error.message}</code>
        </div>
      )}

      {/* De hele lijst en niet alleen het eerste wat ontbreekt. */}
      <ul className="checklist">
        {state.requirements.map((requirement) => (
          <li
            className="checklist__item"
            data-met={requirement.met ? 'true' : undefined}
            key={requirement.key}
          >
            <span aria-hidden="true" className="checklist__mark">
              {requirement.met ? '✓' : '·'}
            </span>
            <span>
              {t(`dashboard.publication.requirement.${requirement.key}`)}
              {requirement.detail !== null && (
                <span className="checklist__detail">{requirement.detail}</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="publication__actions">
        {live ? (
          <button
            className="button button--danger"
            disabled={disabled}
            onClick={() => {
              if (window.confirm(t('dashboard.publication.confirmUnpublish'))) {
                void change('draft')
              }
            }}
            type="button"
          >
            {t('dashboard.publication.unpublish')}
          </button>
        ) : (
          <button
            className="button button--primary"
            // Uitgezet zodra er iets ontbreekt: op een knop drukken die gegarandeerd
            // een foutmelding geeft, is geen keuze maar een val. Wat er ontbreekt
            // staat erboven.
            disabled={disabled || blocking.length > 0}
            onClick={() => {
              void change('published')
            }}
            type="button"
          >
            {t('dashboard.publication.publish')}
          </button>
        )}
        {!live && blocking.length > 0 && (
          <span className="publication__blocked">
            {t('dashboard.publication.blocked', { count: blocking.length })}
          </span>
        )}
      </div>
    </section>
  )
}
